import { Box } from '@usealloy/component-library';
import styled, { css } from 'styled-components';

export const StyledTable = styled.div<{
  is2Dimensional?: boolean;
  explainabilityResultRow?: number;
  explainabilityResultCol?: number;
  highlightedRow?: number | null;
}>`
  position: relative;
  font-family: var(--cl-font-family-mono);
  font-size: var(--cl-font-size-2);
  width: max-content;
  box-sizing: border-box;

  table {
    border-collapse: separate;
    border-spacing: 0;
  }

  & > span {
    &:focus {
      outline: none;
    }
  }

  tr:first-child td {
    border: none;
    border-right: 1px solid var(--cl-gray-400);
    background-color: var(--cl-gray-200);
  }

  tr:first-child td span {
    display: flex;
    justify-content: center;
  }

  tbody > tr > td:nth-of-type(1) {
    border: none;
    border-bottom: 1px solid var(--cl-gray-400);
    background-color: var(--cl-gray-200);
  }

  tr:not(:nth-child(2)) td {
    border-top: none;
  }
  td:not(:nth-child(2)) {
    border-left: none;
  }
  ${({ is2Dimensional }) => !is2Dimensional && `
    tr:nth-child(2) td:last-child {
      border-top-right-radius: var(--cl-radius-1);
    }
  `}
  tr:nth-child(2) td:first-child {
    border-top-left-radius: var(--cl-radius-1);
  }
  tr:last-child td:first-child {
    border-bottom-left-radius: var(--cl-radius-1);
  }
  tr:last-child td:last-child {
    border-bottom-right-radius: var(--cl-radius-1);
  }
  tr:first-child td:nth-child(2) {
    border-top-left-radius: var(--cl-radius-1)
  }
  tr:first-child td:last-child {
    border-top-right-radius: var(--cl-radius-1)
  }
  ${({ explainabilityResultCol }) => typeof explainabilityResultCol === 'number' && `
    tr:first-child td:nth-child(${explainabilityResultCol + 2}) {
      background-color: var(--cl-yellow-100);
    }
  `}
  ${({ explainabilityResultRow }) => typeof explainabilityResultRow === 'number' && `
    tr:nth-child(${explainabilityResultRow + 2}) td:first-child {
      background-color: var(--cl-yellow-100);
    }
  `}

  tbody > tr > td:nth-of-type(1) span {
    display: flex;
    justify-content: center;
  }

  tr:first-child td:first-child {
    background-color: transparent;
    border: none;
  }

  ${({ highlightedRow }) => highlightedRow && (`
    tr:nth-child(${highlightedRow}) td {
      background-color: var(--cl-juicyBlue-50);
      border-bottom: 2px solid var(--cl-juicyBlue-400);
    }
    tr:nth-child(${highlightedRow}) td:first-child {
      background-color: var(--cl-juicyBlue-200);
      border: 1px solid var(--cl-juicyBlue-400);
      border-bottom: 2px solid var(--cl-juicyBlue-400);
      border-left: none;
    }
  `)}

  td {
    min-width: 200px;
    height: 40px;
    padding: 0;
    border: 1px solid var(--cl-gray-400);

    & > span {
      border: 1px solid transparent;
      display: flex;
      justify-content: flex-end;
    }
  }

  .selected {
    border: 1px double var(--cl-juicyBlue-600);
    background-color: var(--cl-juicyBlue-50);
  }

  .data-editor {
    width: 100%;
    background-color: transparent;
    height: 100%;
    text-indent: 10px;

    &:focus-visible {
      border: none;
    }
  }
`;

export const CellBox = styled(Box)<{ cellColor: string | null; hasExplainability: boolean; shouldFadeExplainabilityCell: boolean }>`
  height: 100%;
  ${({ cellColor, hasExplainability, shouldFadeExplainabilityCell }) => {
    if (cellColor) {
      return `background-color: rgb(from #${cellColor} r g b / ${hasExplainability ? '1' : '0.5'});`;
    }
    if (!cellColor && shouldFadeExplainabilityCell) {
      return 'background-color: var(--cl-background-disabled);';
    }
    return '';
  }}
  ${({ shouldFadeExplainabilityCell }) => shouldFadeExplainabilityCell && 'opacity: 0.3;'}
  ${({ hasExplainability, shouldFadeExplainabilityCell }) => (
    hasExplainability && !shouldFadeExplainabilityCell && `
      box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.30);
      border: 1px solid white;
      border-radius: var(--cl-radius-1);
    `
  )}
`;

export const CellLabel = styled.p`
  font-size: var(--cl-font-size-1);
  color: var(--cl-gray-500);
  position: relative;
  bottom: 10px;
  left: 4px;
`;

export const CellDiv = styled.div`
  padding-right: var(--cl-space-4);
  width: 100%;
  text-align: right;
`;

interface MatrixModelWrapperProps {
  is1Dimensional?: boolean;
}

export const MatrixModelWrapper = styled(Box)<MatrixModelWrapperProps>`
  && {
    align-items: flex-start;
    justify-content: flex-start;
    border-top: var(--cl-gray-400) 1px solid;
    flex-direction: column;
    padding: var(--cl-space-3);
    ${({ is1Dimensional }) => is1Dimensional && css`
      padding-top: var(--cl-space-5);
    `}
  }
`;

export const StyledMatrixCell = styled.td`
  &:hover {
    background-color: var(--cl-gray-100);
    cursor: cell;
  }

  &&&:focus {
    border: 1px solid var(--cl-juicyBlue-400);
    background-color: var(--cl-juicyBlue-200);
  }
`;

export const StyledMatrixRowHeader = styled(StyledMatrixCell)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  &&&:focus > div {
    background-color: var(--cl-juicyBlue-200);
  }
`;

export const StyledMatrixRowMenuLabel = styled.div<{ disabled: boolean; highlighted: boolean }>`
  font-size: var(--cl-font-size-1);
  width: min-content;
  padding: 0 var(--cl-space-1);
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ highlighted }) => (highlighted ? 'var(--cl-juicyBlue-200)' : 'var(--cl-gray-300)')};
  border-radius: inherit;
  && {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  }
  ${({ disabled }) => (!disabled && `
    &:hover {
      background-color: var(--cl-gray-500);
    }
  `)}
`;
